import { Env, SessionCookie } from 'auth-common/shared';
import { route } from '../router';

export const SessionCsrfHeaderName = SessionCookie.CsrfHeaderName;

export const getSessionCsrfToken = (): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  return SessionCookie.parseCookieValue(document.cookie, Env.Var.Cookie.SessionCsrfToken);
};

export const hasSessionCookieAuthCredential = (): boolean => getSessionCsrfToken() !== undefined;

export const buildLoginPathWithRedirect = (redirect?: string): string => {
  if (typeof window === 'undefined') return route('/login');
  const { pathname, search, hash } = window.location;
  return route('/login', { query: { redirect: redirect ?? `${pathname}${search}${hash}` } });
};

let hasRedirectedToLogin = false;

// ログイン画面への遷移後にブラウザの「戻る」で bfcache から復元されると、モジュールスコープの
// hasRedirectedToLogin が true のまま復活し以降の 401 が無反応になるため、復元時にリセットする。
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event: PageTransitionEvent) => {
    if (event.persisted) {
      hasRedirectedToLogin = false;
    }
  });
}

// beforeunload の確認ダイアログで「留まる」が選ばれると window.location.href への代入がキャンセルされ、
// このフラグが true のまま残り以降の 401 が無反応になる。ナビゲーションが実際にキャンセルされたと
// 判断できる場合（pagehide が発火せずユーザーが操作を再開した場合）だけ再武装する。
// 一定時間で無条件に再武装すると、ログイン画面のロードが遅い場合に進行中の遷移を再度キャンセルしてしまう。
const rearmOnNavigationCancelled = (): void => {
  const clear = (): void => {
    window.removeEventListener('pointerdown', onUserInteraction);
    window.removeEventListener('keydown', onUserInteraction);
    window.removeEventListener('pagehide', onPagehide);
  };
  const onUserInteraction = (): void => {
    hasRedirectedToLogin = false;
    clear();
  };
  const onPagehide = (): void => {
    clear();
  };
  window.addEventListener('pointerdown', onUserInteraction, { once: true });
  window.addEventListener('keydown', onUserInteraction, { once: true });
  window.addEventListener('pagehide', onPagehide, { once: true });
};

export const redirectToLoginForExpiredSession = (): void => {
  // Multiple API requests can fail together after expiry; only the first one should initiate navigation.
  if (hasRedirectedToLogin) return;
  hasRedirectedToLogin = true;
  window.location.href = buildLoginPathWithRedirect();
  rearmOnNavigationCancelled();
};

export const handleSessionCookieAuthResponse = <ResponseType extends Response>(response: ResponseType): ResponseType => {
  if (response.status === 401) redirectToLoginForExpiredSession();
  return response;
};

export const fetchWithSessionCookieAuth = async (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
  const headers = new Headers(init.headers ?? (input instanceof Request ? input.headers : undefined));
  const csrfToken = getSessionCsrfToken();
  if (csrfToken) headers.set(SessionCsrfHeaderName, csrfToken);
  const response = await window.fetch(input, { ...init, credentials: 'include', headers });
  return handleSessionCookieAuthResponse(response);
};
